import { useEffect, useMemo, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  useMailboxSyncStatusQuery,
  useTriggerMailboxSyncMutation,
} from "../api/queries/userQueries";
import { useOutgoingMailboxQuery } from "../api/queries/organizationQueries";
import { paymentKeys } from "../api/queries/queryKeys";
import { receivablesKeys } from "../api/queries/receivablesQueries";
import {
  useInflightProcessingTask,
  useProcessingServiceTaskPolling,
} from "./useProcessingServiceTaskPolling";

export type MailboxSyncNotifyMode = "toast" | "silent";
export type MailboxRefreshPhase =
  | "idle"
  | "starting"
  | "syncing"
  | "processing"
  | "error";

interface UseMailboxSyncNowOptions {
  notify?: MailboxSyncNotifyMode;
}

function deriveRefreshPhase(
  taskStatus: string | undefined,
  taskPhase: string | null | undefined,
  isTriggeringSync: boolean,
  hasError: boolean,
): MailboxRefreshPhase {
  if (hasError) return "error";
  if (isTriggeringSync) return "starting";
  if (!taskStatus || taskStatus === "completed" || taskStatus === "failed") {
    return "idle";
  }
  if (taskStatus === "pending") return "starting";
  if (taskStatus === "running") {
    return taskPhase === "processing" ? "processing" : "syncing";
  }
  return "idle";
}

export function useMailboxSyncNow(options: UseMailboxSyncNowOptions = {}) {
  const { notify = "toast" } = options;
  const queryClient = useQueryClient();
  const { data: syncStatus } = useMailboxSyncStatusQuery();
  const { data: outgoingMailbox } = useOutgoingMailboxQuery();
  const triggerMailboxSyncMutation = useTriggerMailboxSyncMutation();
  const [jobId, setJobId] = useState<string | null>(null);

  const inflightQuery = useInflightProcessingTask("refresh_emails", {
    enabled: !jobId,
  });

  const taskQuery = useProcessingServiceTaskPolling(jobId);

  // Resume inflight task after page reload
  useEffect(() => {
    const inflightTask = inflightQuery.data;
    if (!inflightTask || jobId) return;
    if (
      inflightTask.status === "pending" ||
      inflightTask.status === "running"
    ) {
      setJobId(inflightTask.job_id);
    }
  }, [inflightQuery.data, jobId]);

  // Invalidate data queries when task completes
  useEffect(() => {
    const status = taskQuery.data?.status;
    if (status !== "completed") return;
    void queryClient.invalidateQueries({ queryKey: paymentKeys.all });
    void queryClient.invalidateQueries({ queryKey: receivablesKeys.all });
    setJobId(null);
  }, [taskQuery.data?.status, queryClient]);

  // Clear job on failure too so UI returns to idle
  useEffect(() => {
    if (taskQuery.data?.status === "failed") {
      setJobId(null);
    }
  }, [taskQuery.data?.status]);

  const isTriggeringSync = triggerMailboxSyncMutation.isPending;
  const hasError = Boolean(syncStatus?.has_error);
  const taskStatus = taskQuery.data?.status;
  const taskPhase = taskQuery.data?.phase ?? taskQuery.data?.payload?.phase as string | undefined;

  const isSyncing =
    taskStatus === "pending" ||
    (taskStatus === "running" && taskPhase !== "processing");
  const isProcessingEmails =
    taskStatus === "running" && taskPhase === "processing";

  const refreshPhase = useMemo<MailboxRefreshPhase>(
    () =>
      deriveRefreshPhase(taskStatus, taskPhase as string | null | undefined, isTriggeringSync, hasError),
    [taskStatus, taskPhase, isTriggeringSync, hasError],
  );

  const canSync = useMemo(() => {
    const usesSharedMailbox = Boolean(outgoingMailbox?.uses_official_email);
    const collectionEmail = usesSharedMailbox
      ? outgoingMailbox?.official_outgoing_email
      : outgoingMailbox?.mailbox_email;
    return usesSharedMailbox
      ? Boolean(collectionEmail?.trim()) &&
          Boolean(outgoingMailbox?.mailbox_user_id)
      : Boolean(outgoingMailbox?.mailbox_connected);
  }, [outgoingMailbox]);

  const handleSyncNow = () => {
    triggerMailboxSyncMutation.mutate(undefined, {
      onSuccess: (data) => {
        const newJobId = data?.job_id;
        if (newJobId) {
          setJobId(newJobId);
        }

        if (notify === "toast") {
          if (data?.status === "skipped") {
            toast.info("Sync not available", {
              description:
                "Configure a collections email before syncing the mailbox.",
            });
          } else if (data?.status === "already_syncing") {
            toast.info("Sync already in progress", {
              description: "Email sync is already running.",
            });
          } else {
            const isNonOwnerSharedMailbox =
              Boolean(outgoingMailbox?.uses_official_email) &&
              !outgoingMailbox?.acting_user_is_mailbox_owner;
            toast.info("Sync started", {
              description: isNonOwnerSharedMailbox
                ? "The shared collections mailbox sync has been started."
                : "Collections mailbox sync has been started.",
            });
          }
        }
      },
      onError: () => {
        toast.error("Error", {
          description: "Failed to start email sync.",
        });
      },
    });
  };

  return {
    syncStatus,
    isSyncing,
    isProcessingEmails,
    isTriggeringSync,
    refreshPhase,
    canSync,
    handleSyncNow,
  };
}
